import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { format, parse, subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConsumptionDayGroup, ConsumptionDayLabel, CreditConsumptionEntry } from 'src/app/models/chat.model';
import { ChatService } from 'src/app/services/chat.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';

const CANONICAL_DAY_FORMAT = 'yyyy-MM-dd';

export function groupConsumptionByDay(
  entries: CreditConsumptionEntry[],
  timeZone: string,
  now: Date,
  userDateFormat: string,
): ConsumptionDayGroup[] {
  const totalsByDay = new Map<string, number>();

  for (const entry of entries) {
    const dayKey = formatInTimeZone(new Date(entry.dateCreated), timeZone, CANONICAL_DAY_FORMAT);
    totalsByDay.set(dayKey, (totalsByDay.get(dayKey) ?? 0) + entry.credits);
  }

  const todayKey = formatInTimeZone(now, timeZone, CANONICAL_DAY_FORMAT);
  const yesterdayKey = format(subDays(parse(todayKey, CANONICAL_DAY_FORMAT, now), 1), CANONICAL_DAY_FORMAT);

  return Array.from(totalsByDay.entries())
    .sort(([a], [b]) => (a > b ? -1 : a < b ? 1 : 0))
    .map(([dayKey, total]) => ({ dayKey, total, label: buildDayLabel(dayKey, todayKey, yesterdayKey, userDateFormat, now) }));
}

function buildDayLabel(
  dayKey: string,
  todayKey: string,
  yesterdayKey: string,
  userDateFormat: string,
  now: Date,
): ConsumptionDayLabel {
  if (dayKey === todayKey) {
    return { type: 'translated', key: 'Today' };
  }
  if (dayKey === yesterdayKey) {
    return { type: 'translated', key: 'Yesterday' };
  }
  return { type: 'absolute', date: format(parse(dayKey, CANONICAL_DAY_FORMAT, now), userDateFormat) };
}

@Component({
  standalone: false,
  selector: 'app-consumption-popover',
  templateUrl: './consumption-popover.component.html',
  styleUrls: ['./consumption-popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsumptionPopoverComponent implements OnInit, OnDestroy {
  groups: ConsumptionDayGroup[] = [];
  isLoading = true;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly chatService: ChatService,
    private readonly dateFormatService: DateFormatService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.chatService
      .getRecentConsumption()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (entries) => {
          this.groups = groupConsumptionByDay(
            entries,
            this.dateFormatService.getTimeZone(),
            new Date(),
            this.dateFormatService.userDateFormat,
          );
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByDay(_: number, group: ConsumptionDayGroup): string {
    return group.dayKey;
  }
}
